import { IArticle, ICreateArticle, IUpdateArticle } from "../models/article.ts";
import { ILazyAttachment } from "../models/zip_content.ts";
import { IArticleAttachment } from "../models/articleAttachment.ts";
import { zendeskHttpRequest } from "../lib/api/zendeskHttpRequest.ts";
import { CancelManager } from "../lib/cancelManager.ts";
import { getArticleAttachments } from "../lib/api/articleAttachments/getArticleAttachments.ts";
import { deleteArticleAttachment } from "../lib/api/articleAttachments/deleteArticleAttachment.ts";

/**
 * インライン画像をアップロードし、記事本文のプレースホルダーを実際の画像URLに置換する
 *
 * 1. 本文から {{inline_images/xxx}} プレースホルダーを抽出
 * 2. 対応する画像ファイルを inline=true でアップロード
 * 3. レスポンスの content_url でプレースホルダーを <img> タグに置換
 * 4. 記事本文を更新
 */
export const handleInlineImages = async (
  token: string,
  brandUrl: string,
  postedArticle: IArticle,
  article: ICreateArticle | IUpdateArticle,
  inlineImages: ILazyAttachment[],
): Promise<IArticle> => {
  const cancelManager = CancelManager.getInstance();

  // 更新記事の場合、既存インライン添付を取得しておく
  let existingInlineAttachments: IArticleAttachment[] = [];
  if ("id" in article) {
    const all = await getArticleAttachments(token, brandUrl, article.id);
    existingInlineAttachments = all.filter((a) => a.inline);
  }

  // プレースホルダーを抽出
  const placeholderRegex = /\{\{(inline_images\/[^}]+)\}\}/g;
  const placeholders: { fullMatch: string; filePath: string }[] = [];
  let match;
  while ((match = placeholderRegex.exec(article.body)) !== null) {
    placeholders.push({ fullMatch: match[0], filePath: match[1] });
  }

  let updatedBody = article.body;

  // 各プレースホルダーに対応する画像をアップロードし、URLを取得
  for (const placeholder of placeholders) {
    cancelManager.checkCancelled();

    const fileName = placeholder.filePath.replace("inline_images/", "");
    const imageFile = inlineImages.find((img) => img.name === fileName);

    if (!imageFile) {
      console.warn(`インライン画像が見つかりません: ${fileName}`);
      continue;
    }

    const uploadedAttachment = await uploadInlineImage(
      token,
      brandUrl,
      postedArticle.id,
      imageFile,
    );

    const imgTag = `<img src="${uploadedAttachment.content_url}" />`;
    updatedBody = updatedBody.replace(placeholder.fullMatch, imgTag);
  }

  // 本文が変わった場合（プレースホルダーが置換された）は記事本文を更新
  if (updatedBody !== article.body) {
    const locale = postedArticle.locale || article.locale;
    await zendeskHttpRequest(
      `help_center/articles/${postedArticle.id}/translations/${locale}`,
      token,
      "PUT",
      brandUrl,
      JSON.stringify({
        translation: {
          body: updatedBody,
        },
      }),
    );
  }

  // 新しい本文に content_url が含まれていない既存インライン添付を削除
  for (const attachment of existingInlineAttachments) {
    cancelManager.checkCancelled();
    if (!updatedBody.includes(attachment.content_url)) {
      await deleteArticleAttachment(token, brandUrl, attachment.id);
    }
  }

  return { ...postedArticle, body: updatedBody };
};

/**
 * 画像を inline=true パラメータ付きで記事にアップロードする
 */
const uploadInlineImage = async (
  token: string,
  brandUrl: string,
  articleId: number,
  imageFile: ILazyAttachment,
): Promise<IArticleAttachment> => {
  const content = imageFile.getContent();
  const blob = new Blob([content.buffer as ArrayBuffer], {
    type: "application/octet-stream",
  });
  const formData = new FormData();
  formData.append("file", blob, imageFile.name);
  formData.append("inline", "true");

  const response = await zendeskHttpRequest<{
    article_attachment: IArticleAttachment;
  }>(
    `help_center/articles/${articleId}/attachments`,
    token,
    "POST",
    brandUrl,
    formData,
  );

  return response.article_attachment;
};
